import React from 'react';
import { SyncInfo } from '../types/Review';
import '../styles/SyncStatus.css';

interface SyncStatusProps {
  syncInfo: SyncInfo;
  onRefresh: () => void;
}

const SyncStatus: React.FC<SyncStatusProps> = ({ syncInfo, onRefresh }) => {
  // Handle null/undefined syncInfo
  if (!syncInfo || !syncInfo.sources) {
    return (
      <div className="sync-status-container">
        <div className="sync-status-loading">Loading sync status...</div>
      </div>
    );
  }
  const formatDate = (dateString: string | null | undefined, isNextSync: boolean = false) => {
    if (!dateString) return 'Never';
    
    try {
      const date = new Date(dateString);
      // Check if date is valid
      if (isNaN(date.getTime())) return 'Never';
      
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const absDiffMs = Math.abs(diffMs);
      const diffHours = Math.floor(absDiffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffHours / 24);
      const diffMinutes = Math.floor(absDiffMs / (1000 * 60));
      
      // For next sync times
      if (isNextSync) {
        if (diffMs > 0) {
          // Next sync is in the past (overdue)
          return 'Overdue';
        } else {
          // Next sync is in the future
          if (diffDays > 0) {
            return `in ${diffDays} day${diffDays > 1 ? 's' : ''}`;
          } else if (diffHours > 0) {
            return `in ${diffHours} hour${diffHours > 1 ? 's' : ''}`;
          } else {
            return `in ${diffMinutes} minute${diffMinutes > 1 ? 's' : ''}`;
          }
        }
      }
      
      // For last sync times (past events)
      if (diffDays > 0) {
        return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
      } else if (diffHours > 0) {
        return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
      } else {
        return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
      }
    } catch (e) {
      return 'Never';
    }
  };

  const getStatusColor = (info: any) => {
    // If there's an error, check if it's recent
    if (info.error) {
      // If the error is about Lambda configuration, it's a warning
      if (info.error.includes(':$LATEST') || info.error.includes('Function not found')) {
        return 'sync-status-warning';
      }
      // If last sync was successful despite error, it might be stale
      if (info.lastSync) {
        try {
          const date = new Date(info.lastSync);
          if (!isNaN(date.getTime())) {
            const now = new Date();
            const diffHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
            if (diffHours < 24) return 'sync-status-info'; // Recent sync despite error
          }
        } catch (e) {}
      }
      return 'sync-status-error';
    }
    
    if (!info.lastSync) return 'sync-status-warning';
    
    try {
      const date = new Date(info.lastSync);
      // Check if date is valid
      if (isNaN(date.getTime())) return 'sync-status-warning';
      
      const now = new Date();
      const diffHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
      
      if (diffHours < 24) return 'sync-status-success';
      if (diffHours < 72) return 'sync-status-info';
      return 'sync-status-warning';
    } catch (e) {
      return 'sync-status-warning';
    }
  };

  const getStatusTooltip = (source: string, info: any) => {
    if (!info.lastSync) {
      return `${source} has never been synced. Click 'Manual Sync' to fetch reviews from ${source}.`;
    }
    
    if (info.error) {
      return `Error syncing ${source}: ${info.error}. Check your API credentials and try again.`;
    }
    
    if (info.status === 'syncing') {
      return `Currently syncing reviews from ${source}. This may take a few moments.`;
    }
    
    try {
      const date = new Date(info.lastSync);
      if (isNaN(date.getTime())) {
        return `Invalid sync date for ${source}. Manual sync may be required.`;
      }
      
      const now = new Date();
      const diffHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
      
      if (diffHours < 1) {
        return `${source} sync is up to date. Last synced less than an hour ago.`;
      } else if (diffHours < 24) {
        return `${source} sync is recent. Last synced ${Math.floor(diffHours)} hours ago.`;
      } else if (diffHours < 72) {
        return `${source} sync is getting stale. Consider syncing to get latest reviews. Last synced ${Math.floor(diffHours / 24)} days ago.`;
      } else {
        return `${source} sync is outdated! Reviews may be missing. Last synced ${Math.floor(diffHours / 24)} days ago. Click 'Manual Sync' to update.`;
      }
    } catch {
      return `Unable to determine sync status for ${source}. Manual sync recommended.`;
    }
  };

  return (
    <div className="sync-status-container">
      <div className="sync-status-header">
        <h3>Sync Status</h3>
        <button onClick={onRefresh} className="refresh-status-btn">
          ↻
        </button>
      </div>
      
      <div className="sync-status-grid">
        {Object.entries(syncInfo.sources).map(([source, info]) => (
          <div key={source} className={`sync-status-card ${getStatusColor(info)}`}>
            <div className="sync-source-header">
              <span className="sync-source-name">{source}</span>
              <span 
                className={`sync-status-indicator ${info.status}`}
                title={getStatusTooltip(source, info)}
                style={{ cursor: 'help' }}
              >
                {info.status === 'syncing' ? '⟳' : info.status === 'success' ? '✓' : '⚠'}
              </span>
            </div>
            
            <div className="sync-details">
              <div className="sync-detail-row">
                <span className="sync-detail-label">Last Sync:</span>
                <span className="sync-detail-value">{formatDate(info.lastSync)}</span>
              </div>
              
              <div className="sync-detail-row">
                <span className="sync-detail-label">Reviews:</span>
                <span className="sync-detail-value">{info.reviewCount}</span>
              </div>
              
              {info.lastSync && (
                <div className="sync-detail-row">
                  <span className="sync-detail-label">Date:</span>
                  <span className="sync-detail-value">
                    {(() => {
                      try {
                        const date = new Date(info.lastSync);
                        return isNaN(date.getTime()) ? 'Invalid date' : date.toLocaleDateString();
                      } catch {
                        return 'Invalid date';
                      }
                    })()}
                  </span>
                </div>
              )}
              
              {info.nextSync && (
                <div className="sync-detail-row">
                  <span className="sync-detail-label">Next:</span>
                  <span className="sync-detail-value">
                    {formatDate(info.nextSync, true)}
                  </span>
                </div>
              )}
              
              {info.error && (
                <div className="sync-error" title={`Error details: ${info.error}`}>
                  <span className="sync-error-icon">⚠</span>
                  <span className="sync-error-text">
                    {info.error.includes(':$LATEST') 
                      ? 'Sync configuration error - deployment update needed'
                      : info.error}
                  </span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      
      <div className="sync-summary">
        <div className="sync-summary-item">
          <span className="sync-summary-label">Total Reviews:</span>
          <span className="sync-summary-value">{syncInfo.totalReviews}</span>
        </div>
        <div className="sync-summary-item">
          <span className="sync-summary-label">Last Updated:</span>
          <span className="sync-summary-value">{formatDate(syncInfo.lastUpdated)}</span>
        </div>
      </div>
    </div>
  );
};

export default SyncStatus;